import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createTrip,
  addTripTransaction,
  getAdminTrips,
  type TripData,
} from '@/lib/firestore-trip';
import { useAuth } from '@/contexts/AuthContext';
import { EXPENSE_CATEGORIES, CURRENCY_SYMBOLS } from '@/types';

interface Props {
  onClose: () => void;
}

type Step = 'list' | 'create' | 'share' | 'add';

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', borderRadius: 10,
  border: '1.5px solid #e2e8f0', background: '#f8fafc',
  fontSize: 14, color: '#0f172a', outline: 'none', boxSizing: 'border-box',
};
const labelStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4,
};

export function TripModal({ onClose }: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('list');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // "My Trips" list state
  const [myTrips, setMyTrips] = useState<TripData[]>([]);
  const [listLoading, setListLoading] = useState(true);

  // Create form
  const [tripId, setTripId] = useState('');
  const [name, setName] = useState('');
  const [fund, setFund] = useState('');
  const [currency, setCurrency] = useState('INR');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Add transaction form
  const [txName, setTxName] = useState('');
  const [txAmount, setTxAmount] = useState('');
  const [txCat, setTxCat] = useState('exp_8');
  const [txNote, setTxNote] = useState('');
  const [txSuccess, setTxSuccess] = useState(false);

  const shareUrl = tripId ? `${window.location.origin}/trip/${tripId}` : '';

  // Load admin's trips on mount
  useEffect(() => {
    if (!user) return;
    setListLoading(true);
    getAdminTrips(user.uid)
      .then(setMyTrips)
      .catch(console.error)
      .finally(() => setListLoading(false));
  }, [user]);

  async function handleCreate() {
    if (!user || !name || !fund || !startDate || !endDate) return;
    setLoading(true);
    try {
      const id = await createTrip(user.uid, {
        name, totalFund: parseFloat(fund), currency, startDate, endDate,
      });
      setTripId(id);
      setStep('share');
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  function handleCopy() {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleAddTx() {
    if (!txName || !txAmount) return;
    setLoading(true);
    try {
      await addTripTransaction(tripId, {
        name: txName, amount: parseFloat(txAmount), categoryId: txCat, notes: txNote,
      });
      setTxName(''); setTxAmount(''); setTxNote('');
      setTxSuccess(true);
      setTimeout(() => setTxSuccess(false), 2000);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  function openTrip(id: string) {
    onClose();
    navigate(`/trip/${id}`);
  }

  const btnPrimary: React.CSSProperties = {
    width: '100%', padding: '11px', borderRadius: 10, border: 'none',
    background: 'linear-gradient(135deg,#3B82F6,#7C3AED)', color: '#fff',
    fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
    opacity: loading ? 0.7 : 1,
  };

  const stepTitles: Record<Step, string> = {
    list: 'My Group Trips',
    create: 'New Group Trip',
    share: 'Share with Friends',
    add: 'Add Expense',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 420, maxHeight: '90vh', overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {(step === 'create' || (step === 'list' && false)) && (
              <button
                onClick={() => setStep('list')}
                style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#94a3b8', padding: '0 4px 0 0' }}
              >
                ←
              </button>
            )}
            <h2 style={{ fontSize: 17, fontWeight: 600, color: '#0f172a', margin: 0 }}>
              {stepTitles[step]}
            </h2>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8' }}>×</button>
        </div>

        {/* ── STEP: list ── */}
        {step === 'list' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Create new button */}
            <button
              style={{ ...btnPrimary, opacity: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              onClick={() => setStep('create')}
            >
              <span style={{ fontSize: 16 }}>+</span> Create New Trip
            </button>

            {/* Divider */}
            {myTrips.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0' }}>
                <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
                <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Your trips</span>
                <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
              </div>
            )}

            {/* Trips list */}
            {listLoading ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: '#94a3b8', fontSize: 13 }}>
                Loading trips…
              </div>
            ) : myTrips.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>✈️</div>
                <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>No trips yet. Create your first one!</p>
              </div>
            ) : (
              myTrips.map((trip) => {
                const sym = CURRENCY_SYMBOLS[trip.currency] ?? trip.currency;
                return (
                  <div
                    key={trip.id}
                    onClick={() => openTrip(trip.id)}
                    style={{
                      border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '12px 14px',
                      cursor: 'pointer', background: '#fafafa', transition: 'border-color 0.15s',
                      display: 'flex', alignItems: 'center', gap: 12,
                    }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = '#7C3AED')}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = '#e2e8f0')}
                  >
                    {/* Icon */}
                    <div style={{
                      width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                      background: 'linear-gradient(135deg,#3B82F620,#7C3AED20)',
                      border: '1.5px solid #7C3AED33',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                    }}>
                      🗺️
                    </div>
                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {trip.name}
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                        {trip.startDate} → {trip.endDate}
                      </div>
                    </div>
                    {/* Fund + arrow */}
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#7C3AED' }}>
                        {sym}{trip.totalFund.toLocaleString('en-IN')}
                      </div>
                      <div style={{ fontSize: 16, color: '#cbd5e1', marginTop: 1 }}>›</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* ── STEP: create ── */}
        {step === 'create' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <button
              onClick={() => setStep('list')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7C3AED', fontSize: 13, fontWeight: 600, textAlign: 'left', padding: 0, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}
            >
              ← Back to trips
            </button>
            <div>
              <label style={labelStyle}>Trip name</label>
              <input style={inputStyle} placeholder="e.g. Goa Trip 2026" value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={labelStyle}>Start date</label>
                <input style={inputStyle} type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>End date</label>
                <input style={inputStyle} type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
              <div>
                <label style={labelStyle}>Total fund</label>
                <input style={inputStyle} type="number" placeholder="e.g. 9000" value={fund} onChange={e => setFund(e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Currency</label>
                <select style={inputStyle} value={currency} onChange={e => setCurrency(e.target.value)}>
                  {Object.keys(CURRENCY_SYMBOLS).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <button style={btnPrimary} onClick={handleCreate} disabled={loading}>
              {loading ? 'Creating...' : 'Create Trip →'}
            </button>
          </div>
        )}

        {/* ── STEP: share ── */}
        {step === 'share' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 12 }}>
              <p style={{ fontSize: 13, color: '#166534', margin: 0 }}>Trip created! Share this link with your friends. They can view it in real time — no login needed.</p>
            </div>
            <div>
              <label style={labelStyle}>Shareable link</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input style={{ ...inputStyle, flex: 1, fontSize: 12, color: '#3B82F6' }} value={shareUrl} readOnly />
                <button onClick={handleCopy} style={{ padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: copied ? '#f0fdf4' : '#f8fafc', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: copied ? '#166534' : '#475569', whiteSpace: 'nowrap' }}>
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            <button style={btnPrimary} onClick={() => setStep('add')}>
              Add First Expense →
            </button>
            <button onClick={() => openTrip(tripId)} style={{ ...btnPrimary, background: '#f1f5f9', color: '#475569' }}>
              Open Trip Dashboard
            </button>
          </div>
        )}

        {/* ── STEP: add ── */}
        {step === 'add' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {txSuccess && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 10 }}>
                <p style={{ fontSize: 13, color: '#166534', margin: 0 }}>Expense added! Visible to all members now.</p>
              </div>
            )}
            <div>
              <label style={labelStyle}>Expense name</label>
              <input style={inputStyle} placeholder="e.g. Beach dinner" value={txName} onChange={e => setTxName(e.target.value)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={labelStyle}>Amount</label>
                <input style={inputStyle} type="number" placeholder="0" value={txAmount} onChange={e => setTxAmount(e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Category</label>
                <select style={inputStyle} value={txCat} onChange={e => setTxCat(e.target.value)}>
                  {Object.entries(EXPENSE_CATEGORIES).map(([id, names]) => (
                    <option key={id} value={id}>{names.en}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Note (optional)</label>
              <input style={inputStyle} placeholder="Any details..." value={txNote} onChange={e => setTxNote(e.target.value)} />
            </div>
            <button style={btnPrimary} onClick={handleAddTx} disabled={loading}>
              {loading ? 'Adding...' : 'Add Expense'}
            </button>
            <button onClick={() => openTrip(tripId)} style={{ ...btnPrimary, background: 'linear-gradient(135deg,#059669,#10B981)' }}>
              Open Trip Dashboard →
            </button>
            <button onClick={onClose} style={{ ...btnPrimary, background: '#f1f5f9', color: '#475569' }}>
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
